// Turnstile widget helper: explicit render inside a modal, resolving one
// single-use token per call. Tokens are consumed by exactly one siteverify,
// so every gated action (each vote, each suggestion) requests a fresh one.
import { $ } from './dom';
import { state } from './state';

interface TurnstileApi {
  render(
    el: HTMLElement,
    opts: {
      sitekey: string;
      action: string;
      theme?: 'light' | 'dark' | 'auto';
      callback: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
    },
  ): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __turnstileOnload?: () => void;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      window.__turnstileOnload = () => resolve(window.turnstile!);
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__turnstileOnload&render=explicit';
      script.async = true;
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('failed to load Turnstile — check your network'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/**
 * Show the challenge modal and resolve with a fresh token, or null if the
 * user cancelled. Rejects only if the Turnstile script cannot load.
 */
export async function getTurnstileToken(action: 'vote' | 'suggest'): Promise<string | null> {
  const turnstile = await loadTurnstile();
  const dialog = $<HTMLDialogElement>('#turnstileDialog');
  const container = $<HTMLElement>('#turnstileContainer');
  container.innerHTML = '';

  return new Promise((resolve) => {
    let widgetId: string | null = null;
    let settled = false;

    const finish = (token: string | null) => {
      if (settled) return;
      settled = true;
      dialog.removeEventListener('close', onClose);
      if (dialog.open) dialog.close();
      if (widgetId !== null) {
        try {
          turnstile.remove(widgetId);
        } catch {
          // widget already gone
        }
      }
      container.innerHTML = '';
      resolve(token);
    };
    const onClose = () => finish(null);

    dialog.addEventListener('close', onClose);
    dialog.showModal();
    widgetId = turnstile.render(container, {
      sitekey: state.config.sitekey,
      action,
      theme: 'dark',
      callback: (token) => finish(token),
      'error-callback': () => finish(null),
      'expired-callback': () => finish(null),
    });
  });
}
